// Thin JSON client used by both the customer and admin SPAs. Sends
// CSRF tokens for unsafe verbs (Rails strips the token from session
// requests otherwise) and converts non-2xx responses into a structured
// `ApiError` whose `.code` matches the server's reason-code convention
// (e.g. `outside_us`, `yard_has_units`).

export type Json = Record<string, unknown> | unknown[] | string | number | boolean | null;

export interface FetchJsonInit extends Omit<RequestInit, "body"> {
  body?: Json;
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, body: unknown) {
    super(`API ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }

  // Convenience for server-provided reason codes ({ "error": "outside_us" }).
  get code(): string | null {
    if (this.body && typeof this.body === "object" && "error" in this.body) {
      const err = (this.body as { error?: unknown }).error;
      return typeof err === "string" ? err : null;
    }
    return null;
  }
}

export function csrfToken(): string | null {
  const meta = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']");
  return meta?.content ?? null;
}

const UNSAFE = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export async function fetchJson<T = unknown>(url: string, init: FetchJsonInit = {}): Promise<T> {
  const { body, headers: hdrInit, ...rest } = init;
  const method = (rest.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(hdrInit as Record<string, string> | undefined)
  };

  if (UNSAFE.has(method)) {
    const tok = csrfToken();
    if (tok) headers["X-CSRF-Token"] = tok;
  }

  const requestInit: RequestInit = {
    ...rest,
    method,
    headers,
    credentials: "same-origin"
  };
  if (body !== undefined && body !== null) {
    requestInit.body = JSON.stringify(body);
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(url, requestInit);

  if (res.status === 204) {
    return null as T;
  }

  // Try to parse a body for both success and error paths so callers
  // always get something useful in `ApiError.body`.
  let payload: unknown = null;
  const text = await res.text();
  if (text.length > 0) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!res.ok) {
    throw new ApiError(res.status, payload);
  }
  return payload as T;
}
